import { ChevronLeftIcon, PaperAirplaneIcon, ShareIcon } from '@heroicons/react/24/outline'
import { MessageCircleMore } from 'lucide-react'
import { useRef } from 'react'
import { useNavigate, useParams } from 'react-router'
import { FormWrapper } from '@/form-control'
import { TextAreaField } from '@/form-control/fields'
import ROUTES from '@/ROUTES'
import { formatDistanceToNow } from 'date-fns'

import { IComment } from '@/MODELS/comment.model'
import { IPost } from '@/MODELS/post.model'

import { useAuth } from '@/services/authentication.service'
import { useResource } from '@/services/resource.service'

import Avatar from '@/components/Avatar'
import Button from '@/components/Button'
import { SingleDataView } from '@/components/data-view/SingleDataView'
import Loading from '@/components/Loading'
import { Reply } from '@/components/Reply'
import S3InlineViewer from '@/components/s3/S3InlineViewer'

import { PostFooter } from '../app/CommunityPage'

export default function ViewPostPage() {
    const navigate = useNavigate()
    const { user } = useAuth()
    const params = useParams()

    const id = params.id

    const commentsRef = useRef(null)

    const [comments, _, CommentResource] = useResource<IComment[]>(`${ROUTES.app.comment}?post=${id}`)

    const handleShare = async (post: IPost) => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `Post by ${post.owner?.name || 'Anonymous'}`,
                    text: post.content.substring(0, 100) + '...',
                    url: window.location.href,
                })
            } catch (error) {
                console.log('Error sharing:', error)
            }
        } else {
            navigator.clipboard.writeText(window.location.href)
        }
    }
    const topLevelComments =
        comments?.filter(
            (comment) =>
                !comment.parentComment || (Array.isArray(comment.parentComment) && comment.parentComment.length === 0)
        ) || []

    const replyComments = comments?.filter((comment) => comment.parentComment !== null) || []

    return (
        <SingleDataView<IPost> endpoint={`${ROUTES.app.post}/${id}`}>
            {({ data, isLoading, reload }) => (
                <div className='min-h-screen bg-white'>
                    <div className='sticky top-0 z-10 border-b border-gray-100 bg-white/90 backdrop-blur-md'>
                        <div className='max-w-2xl px-4 py-4 mx-auto'>
                            <div className='flex items-center space-x-4'>
                                <button
                                    onClick={() => navigate(-1)}
                                    className='p-1.5 -ml-1.5 rounded-full hover:bg-gray-100 transition-colors'
                                >
                                    <ChevronLeftIcon className='w-5 h-5 text-gray-600' />
                                </button>
                                <h1 className='text-sm font-medium text-gray-900'>Thread</h1>
                            </div>
                        </div>
                    </div>

                    <section className='max-w-2xl px-4 py-8 mx-auto'>
                        <Loading isLoading={isLoading} mode='block' text='Loading...' />

                        {data !== null && (
                            <>
                                <article className='mb-8'>
                                    <div className='flex items-center mb-4 space-x-3'>
                                        <Avatar user={data.owner} size='sm' />
                                        <div>
                                            <p className='font-medium text-gray-900'>
                                                {data.owner?.name || 'Anonymous'}
                                            </p>
                                            <p className='text-sm text-gray-500'>
                                                {formatDistanceToNow(data.createdAt)} ago
                                            </p>
                                        </div>
                                    </div>

                                    <div className='mb-6'>
                                        <p className='leading-relaxed text-gray-900 whitespace-pre-wrap'>
                                            {data.content}
                                        </p>

                                        {data.media && data.media.length > 0 && (
                                            <div className='mt-4 space-y-3'>
                                                {data.media.map((key, index) => (
                                                    <div key={index} className='overflow-hidden rounded-lg bg-gray-50'>
                                                        <S3InlineViewer s3ObjectId={key} className='w-full h-auto' />
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className='flex items-center justify-between py-3 border-t border-b border-gray-100'>
                                        <PostFooter post={data} user={user} get={reload} />
                                        <button
                                            onClick={() => handleShare(data)}
                                            className='p-2 transition-colors rounded-full hover:bg-gray-100'
                                        >
                                            <ShareIcon className='w-4 h-4 text-gray-600' />
                                        </button>
                                    </div>
                                </article>

                                <div className='p-6 mb-8 border-t rounded-xl'>
                                    <h3 className='mb-4 text-lg font-semibold text-gray-900'>Join the conversation</h3>
                                    <FormWrapper<IComment>
                                        endpoint={ROUTES.app.comment}
                                        id='new'
                                        insertIntoPostBody={{ owner: user, post: data }}
                                        hideSubmitButton
                                    >
                                        {(f, { formValues, submit }) => (
                                            <div className='relative'>
                                                <div className='flex space-x-3'>
                                                    <Avatar user={user} size='sm' />
                                                    <div className='flex-1'>
                                                        <TextAreaField
                                                            label=''
                                                            {...f('body')}
                                                            placeholder='Share your thoughts...'
                                                            className='w-full min-h-[100px] px-4 py-3 border border-gray-200 rounded-xl resize-none focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all'
                                                        />
                                                        <div className='flex justify-end mt-3'>
                                                            <Button.Outline
                                                                disabled={!formValues?.body?.trim()}
                                                                className='flex items-center px-6 py-2 space-x-2 text-white transition-all bg-brand-600 rounded-xl hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed'
                                                                onClickAsync={async () => {
                                                                    await submit()
                                                                    reload()
                                                                    CommentResource.get()
                                                                }}
                                                            >
                                                                <span>Comment</span>
                                                                <PaperAirplaneIcon className='w-4 h-4' />
                                                            </Button.Outline>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </FormWrapper>
                                </div>

                                <div>
                                    {comments && comments.length > 0 && (
                                        <h3 className='mb-6 text-sm font-medium text-gray-500'>
                                            {comments.length} {comments.length === 1 ? 'comment' : 'comments'}
                                        </h3>
                                    )}

                                    <div ref={commentsRef}>
                                        {!comments || comments.length === 0 ? (
                                            <div className='py-12 text-center'>
                                                <MessageCircleMore className='w-8 h-8 mx-auto mb-3 text-gray-300' />
                                                <p className='text-gray-500'>No comments yet</p>
                                            </div>
                                        ) : (
                                            <div className='space-y-6'>
                                                {topLevelComments.map((comment) => (
                                                    <Reply
                                                        key={comment._id}
                                                        comment={comment}
                                                        post={data}
                                                        depth={0}
                                                        get={() => {
                                                            CommentResource.get()
                                                        }}
                                                        replyComments={replyComments}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </section>
                </div>
            )}
        </SingleDataView>
    )
}
